import type { Report } from '#lib/types';

export type BadgeTheme = 'light' | 'dark';
export type BadgeSize = 'small' | 'large';

export interface BadgeInfo {
	id: string;
	hash: string;
	date: string;
	score: number;
	grade: string;
}

const GRADE_COLOR: Record<string, string> = {
	A: '#15803d',
	B: '#1d4ed8',
	C: '#c2410c',
	D: '#b91c1c'
};

export function gradeColor(grade: string): string {
	return GRADE_COLOR[grade] ?? '#57534e';
}

function simpleHash(text: string): string {
	let h = 2166136261;
	for (let i = 0; i < text.length; i++) {
		h ^= text.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return (h >>> 0).toString(16).padStart(8, '0');
}

async function sha256(text: string): Promise<string> {
	try {
		if (typeof crypto !== 'undefined' && crypto.subtle) {
			const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
			return Array.from(new Uint8Array(buf))
				.slice(0, 4)
				.map((b) => b.toString(16).padStart(2, '0'))
				.join('');
		}
	} catch {
		/* fallback */
	}
	return simpleHash(text);
}

export async function createBadgeInfo(report: Report): Promise<BadgeInfo> {
	const date = report.generatedAt.slice(0, 10).replace(/-/g, '');
	const hash = await sha256(report.document.fileName);
	return {
		id: `RC-${hash}-${date}`,
		hash,
		date,
		score: report.scores.overall,
		grade: report.scores.grade
	};
}

function esc(s: string): string {
	return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function badgeSvg(
	info: BadgeInfo,
	theme: BadgeTheme = 'light',
	size: BadgeSize = 'small'
): string {
	const value = gradeColor(info.grade);
	const labelBg = theme === 'dark' ? '#0c0a09' : '#1c1917';
	const bg = theme === 'dark' ? '#18181b' : '#ffffff';
	const fg = theme === 'dark' ? '#fafaf9' : '#1c1917';
	const sub = theme === 'dark' ? '#a1a1aa' : '#57534e';
	const font = "system-ui,-apple-system,'Segoe UI',sans-serif";

	if (size === 'large') {
		return `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="80" viewBox="0 0 300 80" role="img" aria-label="Checked with RepCheck, score ${info.score}/${info.grade}">
<rect width="300" height="80" rx="10" fill="${bg}" stroke="${sub}" stroke-opacity="0.35"/>
<rect width="8" height="80" rx="4" fill="${value}"/>
<text x="22" y="30" font-family="${font}" font-size="13" font-weight="700" fill="${fg}">✓ Checked with RepCheck</text>
<text x="22" y="54" font-family="${font}" font-size="20" font-weight="700" fill="${value}">Score: ${info.score}/${info.grade}</text>
<text x="22" y="69" font-family="${font}" font-size="8" fill="${sub}">${esc(info.id)}</text>
</svg>`;
	}

	const h = 30;
	const w = 210;
	return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="Checked with RepCheck, score ${info.score}/${info.grade}">
<rect width="${w}" height="${h}" rx="5" fill="${labelBg}"/>
<rect x="118" width="${w - 118}" height="${h}" rx="5" fill="${value}"/>
<rect x="112" width="12" height="${h}" fill="${value}"/>
<text x="10" y="19" font-family="${font}" font-size="11" font-weight="700" fill="#fafaf9">RepCheck</text>
<text x="124" y="19" font-family="${font}" font-size="11" font-weight="700" fill="#ffffff">✓ ${info.score}/${info.grade}</text>
<text x="170" y="19" font-family="${font}" font-size="8" fill="#ffffff" opacity="0.8">${esc(info.id.slice(-8))}</text>
</svg>`;
}

export function svgDataUri(svg: string): string {
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function badgeHtml(info: BadgeInfo, theme: BadgeTheme, size: BadgeSize): string {
	const uri = svgDataUri(badgeSvg(info, theme, size));
	return `<a href="https://repcheck.pages.dev" target="_blank" rel="noopener"><img src="${uri}" alt="Checked with RepCheck — score ${info.score}/${info.grade}" width="${size === 'large' ? 300 : 210}" height="${size === 'large' ? 80 : 30}" /></a>`;
}

export function badgeMarkdown(info: BadgeInfo, theme: BadgeTheme, size: BadgeSize): string {
	const uri = svgDataUri(badgeSvg(info, theme, size));
	return `[![Checked with RepCheck — score ${info.score}/${info.grade}](${uri})](https://repcheck.pages.dev)`;
}

export function badgeText(info: BadgeInfo): string {
	return `✓ Checked with RepCheck — Score: ${info.score}/${info.grade} (${info.id})`;
}

export async function svgToPngBlob(svg: string, scale = 2): Promise<Blob> {
	const img = new Image();
	const uri = svgDataUri(svg);
	await new Promise<void>((resolve, reject) => {
		img.onload = () => resolve();
		img.onerror = () => reject(new Error('badge image failed'));
		img.src = uri;
	});
	const canvas = document.createElement('canvas');
	canvas.width = img.width * scale;
	canvas.height = img.height * scale;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('canvas unavailable');
	ctx.scale(scale, scale);
	ctx.drawImage(img, 0, 0);
	return await new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png failed'))), 'image/png');
	});
}
